import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import {
  FiDollarSign,
  FiClock,
  FiAlertTriangle,
  FiAlertCircle,
  FiCheckCircle,
  FiActivity,
} from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { getArAgingApi } from "../../../../Api/ClientWork/ClientWork";
import { useReportData } from "./useReportData";

const ClientARAgingReport = () => {
  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [bracketFilter, setBracketFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const data = useReportData(getArAgingApi, dateRange);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalAR = data.reduce((s, r) => s + (r.netAmount || 0), 0);
    const b0_15 = data
      .filter((r) => r.agingBucket === "0 – 15 Days")
      .reduce((s, r) => s + (r.netAmount || 0), 0);
    const b16_30 = data
      .filter((r) => r.agingBucket === "16 – 30 Days")
      .reduce((s, r) => s + (r.netAmount || 0), 0);
    const b31_45 = data
      .filter((r) => r.agingBucket === "31 – 45 Days")
      .reduce((s, r) => s + (r.netAmount || 0), 0);
    const bOver45 = data
      .filter((r) => r.agingBucket === "Over 45 Days")
      .reduce((s, r) => s + (r.netAmount || 0), 0);

    return {
      totalAR,
      b0_15,
      b16_30,
      b31_45,
      bOver45,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Invoice Number",
        field: "invoiceNo",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Client Name",
        field: "clientName",
        flex: 1.8,
        minWidth: 200,
      },
      {
        headerName: "Project",
        field: "projectName",
        flex: 1.6,
        minWidth: 170,
      },
      {
        headerName: "Submission Date",
        field: "submissionDate",
        flex: 1.2,
        minWidth: 130,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY") : "—"),
      },
      {
        headerName: "Net Invoiced Amount",
        field: "netAmount",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Days Overdue",
        field: "daysOverdue",
        flex: 1.1,
        minWidth: 110,
        cellRenderer: (p) => {
          const days = Number(p.value || 0);
          return (
            <span
              style={{
                fontWeight: 700,
                color: days > 45 ? "#dc2626" : days > 30 ? "#d97706" : "#475569",
              }}
            >
              {days} days
            </span>
          );
        },
      },
      {
        headerName: "Aging Bracket",
        field: "agingBucket",
        flex: 1.3,
        minWidth: 130,
        cellRenderer: (p) => {
          const bucket = p.value;
          let type = "info";
          if (bucket === "0 – 15 Days") {
            type = "success";
          } else if (bucket === "16 – 30 Days") {
            type = "info";
          } else if (bucket === "31 – 45 Days") {
            type = "warning";
          } else if (bucket === "Over 45 Days") {
            type = "error";
          }
          return <StatusChip status={bucket} type={type} />;
        },
      },
      {
        headerName: "Contact Person & Phone",
        field: "contactPerson",
        flex: 1.8,
        minWidth: 190,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "12px" }}>{p.value}</div>
            <div style={{ color: "#64748b", fontSize: "11px" }}>{p.data?.contactPhone}</div>
          </div>
        ),
      },
      {
        headerName: "Status / Recovery Action",
        field: "statusAction",
        flex: 2.2,
        minWidth: 240,
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.invoiceNo && item.invoiceNo.toLowerCase().includes(q)) ||
        (item.clientName && item.clientName.toLowerCase().includes(q)) ||
        (item.projectName && item.projectName.toLowerCase().includes(q)) ||
        (item.contactPerson && item.contactPerson.toLowerCase().includes(q)) ||
        (item.statusAction && item.statusAction.toLowerCase().includes(q));

      const matchesBracket =
        bracketFilter === "all" ||
        item.agingBucket?.toLowerCase().replace(/\s+/g, "") ===
          bracketFilter.toLowerCase().replace(/\s+/g, "");

      return matchesSearch && matchesBracket;
    });
  }, [data, searchTerm, bracketFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback((field) => {
    if (!gridRef.current?.columnApi) {
      return;
    }
    const col = gridRef.current.columnApi.getColumn(field);
    if (col) {
      const isVisible = col.isVisible();
      gridRef.current.columnApi.setColumnVisible(field, !isVisible);
      setColumnVisibility((prev) => ({ ...prev, [field]: !isVisible }));
    }
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Client_AR_Aging_Report_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("AR aging report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="TOTAL RECEIVABLES"
            value={`PKR ${(kpis.totalAR / 1000000).toFixed(2)}M`}
            subtitle="Outstanding portfolio"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="0 – 15 DAYS (CURRENT)"
            value={`PKR ${(kpis.b0_15 / 1000000).toFixed(2)}M`}
            subtitle="Normal processing"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="16 – 30 DAYS (DUE)"
            value={`PKR ${(kpis.b16_30 / 1000000).toFixed(2)}M`}
            subtitle="Routine follow-up"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="31 – 45 DAYS (OVERDUE)"
            value={`PKR ${(kpis.b31_45 / 1000000).toFixed(2)}M`}
            subtitle="Notice issued"
            icon={FiAlertTriangle}
            color="#ea580c"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="OVER 45 DAYS"
            value={`PKR ${(kpis.bOver45 / 1000000).toFixed(2)}M`}
            subtitle="Risk / Escalation"
            icon={FiAlertCircle}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="ESCALATION RATIO"
            value={`${kpis.totalAR > 0 ? ((kpis.bOver45 / kpis.totalAR) * 100).toFixed(1) : 0}%`}
            subtitle="Overdue > 45 days"
            icon={FiActivity}
            color="#64748b"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Client AR Aging Report"
            >
              Client AR Aging Report
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search invoice, client, project, contact..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={bracketFilter}
                  onChange={(e) => setBracketFilter(e.target.value)}
                >
                  <option value="all">All Aging Brackets</option>
                  <option value="0 – 15 Days">0 – 15 Days</option>
                  <option value="16 – 30 Days">16 – 30 Days</option>
                  <option value="31 – 45 Days">31 – 45 Days</option>
                  <option value="Over 45 Days">Over 45 Days</option>
                </select>
                <label>Aging Bracket</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientARAgingReport;
